import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { FieldRenderContext } from '@cli-types/next-adapter/forms.js'
import { renderListField } from '@next-adapter/generators/form/field-jsx-nested/render-list-field.js'
import { getFormFieldType } from '@next-adapter/generators/form/zod-schema/get-form-field-type.js'
import { renderBooleanField } from './render-boolean-field.js'
import { renderDateField } from './render-date-field.js'
import { renderDefaultField } from './render-default-field.js'
import { renderGalleryField } from './render-gallery-field.js'
import { renderGroupField } from './render-group-field.js'
import { renderIconField } from './render-icon-field.js'
import { renderMarkdownField } from './render-markdown-field.js'
import { renderMediaField } from './render-media-field.js'
import { renderNumberField } from './render-number-field.js'
import { renderRelationshipField } from './render-relationship-field.js'
import { renderRichtextField } from './render-richtext-field.js'
import { renderSelectField } from './render-select-field.js'
import { renderSeparatorField } from './render-separator-field.js'
import { renderTextareaField } from './render-textarea-field.js'

export function generateFieldJSXCore(
  field: SchemaField,
  indent = '          ',
  context: FieldRenderContext = {}
): string {
  const fieldType = getFormFieldType(field)
  const label = field.label || field.name
  const hintJSX = field.hint ? `<FormDescription>${field.hint}</FormDescription>` : ''

  if ((field.type === 'group' || field.type === 'section') && field.fields)
    return renderGroupField(field, indent, label, context)
  if (field.type === 'separator') return renderSeparatorField(field, indent)
  if (field.type === 'boolean') return renderBooleanField(field, indent, label, hintJSX, context)
  if (field.type === 'image')
    return renderMediaField(field, indent, label, hintJSX, context, 'image/*')
  if (field.type === 'video')
    return renderMediaField(field, indent, label, hintJSX, context, 'video/*')
  if (field.type === 'media') return renderMediaField(field, indent, label, hintJSX, context)
  if (field.type === 'gallery') return renderGalleryField(field, indent, label, hintJSX, context)
  if (field.type === 'icon') return renderIconField(field, indent, label, hintJSX)
  if (field.type === 'date') return renderDateField(field, indent, label, hintJSX, context)
  if (field.type === 'select') return renderSelectField(field, indent, label, hintJSX, context)
  if (field.type === 'richtext') return renderRichtextField(field, indent, label, hintJSX, context)
  if (field.type === 'markdown') return renderMarkdownField(field, indent, label, hintJSX, context)
  if (field.type === 'text') return renderTextareaField(field, indent, label, hintJSX, context)
  if (field.type === 'number' || field.type === 'decimal')
    return renderNumberField(field, indent, label, fieldType, hintJSX, context)
  if (field.type === 'relationship' && field.relationship)
    return renderRelationshipField(field, indent, label, hintJSX, context)
  if (field.type === 'list') return renderListField(field, indent, label)

  return renderDefaultField(field, indent, label, fieldType, hintJSX, context)
}
